import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Heart, MapPin, Star } from '@phosphor-icons/react';
import { formatPrice, initials } from '../../lib/format';

/** Field subset PropertyCard renders — satisfied by both the landing
 *  FeaturedStayDTO and the search SearchResultCard. */
export interface PropertyCardStay {
  id: string;
  name: string;
  location: string;
  imageUrl: string;
  guests: number;
  beds: number;
  baths: number;
  areaSqft: number;
  pricePerNight: number;
  currency: string;
  rating: number;
}

interface PropertyCardProps {
  stay: PropertyCardStay;
}

/**
 * Hotel result card (F3) — Warm Editorial v2. The card body is a single Link →
 * /hotel/:id (one tab stop). The favourite heart is a separate toggle —
 * LOCAL-ONLY, no persistence, resets on reload (deliberate per scope) — and
 * stops propagation so it never triggers nav. Hotels-only framing: no
 * apartment-style beds/baths/sqft chips.
 */
export function PropertyCard({ stay }: PropertyCardProps) {
  const [saved, setSaved] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-sand bg-white transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-28px_rgba(28,26,23,0.55)]">
      <button
        type="button"
        aria-pressed={saved}
        aria-label={saved ? `Remove ${stay.name} from saved` : `Save ${stay.name}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setSaved((s) => !s);
        }}
        className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-cream/95 shadow-sm backdrop-blur transition hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay"
      >
        <Heart
          weight={saved ? 'fill' : 'light'}
          className={`h-5 w-5 ${saved ? 'text-clay' : 'text-ink'}`}
        />
      </button>

      <Link
        to={`/hotel/${stay.id}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-inset"
        aria-label={`View ${stay.name}, ${stay.location}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-clay-light">
          {imgError ? (
            <div className="flex h-full w-full items-center justify-center font-serif text-3xl font-semibold text-clay">
              {initials(stay.name)}
            </div>
          ) : (
            <img
              src={stay.imageUrl}
              alt={`${stay.name}, ${stay.location}`}
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-cream/95 px-2.5 py-1 text-sm font-semibold text-ink backdrop-blur">
            <Star weight="fill" className="h-3.5 w-3.5 text-clay" aria-hidden="true" />
            {stay.rating.toFixed(1)}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-xl font-semibold text-ink">
          <Link to={`/hotel/${stay.id}`} className="hover:text-clay focus-visible:outline-none">
            {stay.name}
          </Link>
        </h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-muted">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          {stay.location}
        </p>

        <div className="mt-auto flex items-end justify-between border-t border-sand pt-5">
          <p className="text-ink">
            <span className="text-xs uppercase tracking-wide text-ink-muted">from </span>
            <span className="font-serif text-lg font-semibold">
              {formatPrice(stay.pricePerNight, stay.currency)}
            </span>
            <span className="text-sm text-ink-muted"> / night</span>
          </p>
          <Link
            to={`/hotel/${stay.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-clay px-4 py-1.5 text-sm font-semibold text-clay transition hover:bg-clay hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:ring-offset-white"
          >
            View hotel
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}
